import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Zap } from 'lucide-react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import {
  formatNumber,
  formatTimeSavedParts,
  formatDateRangeForDisplay,
} from '@/utils/reporting-formatters'
import { REPORTING_LABELS } from '@/utils/reporting-constants'
import type { ActionMetricsResponse } from '@/lib/api/reporting.client'
import { NEUTRAL_CHART_COLORS } from '@/lib/constants/chart-colors'
import { MetricCard } from '@/components/ui/metric-card'
import { FormattedMetric } from '@/components/ui/formatted-metric'

interface ActionsTabProps {
  actionMetrics: ActionMetricsResponse | null
  dateRange: string
  appliedCustomDates: { start: string; end: string } | null
  isLoading: boolean
}

export function ActionsTab({
  actionMetrics,
  dateRange,
  appliedCustomDates,
  isLoading,
}: ActionsTabProps) {
  const dateRangeText = formatDateRangeForDisplay(dateRange, appliedCustomDates)
  if (isLoading) {
    return (
      <div className="space-y-6 mt-6 animate-in fade-in duration-300">
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map(i => (
            <Card key={i} className="border border-gray-200">
              <CardContent className="p-3 relative">
                <Skeleton className="absolute -top-2 -left-2 h-8 w-8 rounded-lg" />
                <Skeleton className="h-4 w-24 mx-auto mb-3 mt-2" />
                <Skeleton className="h-10 w-16 mx-auto mb-3" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader>
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader>
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!actionMetrics) {
    return null
  }

  // Neutral color palette from Tailwind config
  // These colors are defined in global.css and can be used as CSS classes (e.g., bg-neutral-blue-1)
  const neutralColors = NEUTRAL_CHART_COLORS

  // Helper function to safely parse a number value
  const safeNumber = (value: number | undefined | null): number => {
    if (value === undefined || value === null || isNaN(value) || value < 0) {
      return 0
    }
    return Math.max(0, Math.floor(value))
  }

  // Prepare data for charts with neutral colors
  // Build action type data with renamed labels and break down Push Integration by type
  // Always include all action types, even if zero
  const actionTypeData: Array<{ name: string; value: number; color: string }> =
    []

  // Add Phone Calls (renamed) - always include
  actionTypeData.push({
    name: 'Call Transfers Initiated',
    value: safeNumber(actionMetrics.byType.PHONE_CALL),
    color: neutralColors[actionTypeData.length % neutralColors.length],
  })

  // Add Emails (renamed) - always include
  actionTypeData.push({
    name: 'Email Notifications Sent',
    value: safeNumber(actionMetrics.byType.EMAIL),
    color: neutralColors[actionTypeData.length % neutralColors.length],
  })

  // Add SMS - always include
  actionTypeData.push({
    name: 'SMS Notifications Sent',
    value: safeNumber(actionMetrics.byType.SMS),
    color: neutralColors[actionTypeData.length % neutralColors.length],
  })

  // Add Push Integration - always include
  actionTypeData.push({
    name: 'Integrations Based Actions',
    value: safeNumber(actionMetrics.byType.PUSH_INTEGRATION),
    color: neutralColors[actionTypeData.length % neutralColors.length],
  })

  // Sort workflows by count (descending) and take top 10
  const workflowData = [...actionMetrics.byWorkflow]
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
    .map((workflow, index) => ({
      name: workflow.workflowName,
      count: workflow.count,
      color: neutralColors[index % neutralColors.length],
    }))

  const timeSavedSeconds = actionMetrics.summary.totalTimeSaved ?? 0

  return (
    <div className="space-y-6 mt-6 animate-in fade-in duration-300">
      {/* Summary Stats Row */}
      <div className="grid gap-4 md:grid-cols-2">
        <MetricCard
          icon={Zap}
          title="Actions"
          metric={formatNumber(actionMetrics.summary.totalActions)}
          description="Total automated actions executed"
          info={`Actions measures the total number of automated actions executed by AI, including calls, emails, SMS, and integration-based actions. ${formatNumber(actionMetrics.summary.totalActions)} actions were executed in ${dateRangeText}.`}
        />

        <MetricCard
          icon={Zap}
          title={REPORTING_LABELS.ESTIMATED_TIME_SAVED}
          metric={
            <FormattedMetric
              parts={formatTimeSavedParts(timeSavedSeconds).parts}
            />
          }
          description="Estimated from actions executed"
          info={(() => {
            const { parts } = formatTimeSavedParts(timeSavedSeconds)
            const timeSavedFormatted = parts
              .map(p => `${p.value}${p.label}`)
              .join(' ')
            return `Estimated time saved is based on average completion time per automated action type. Total estimated time saved was ${timeSavedFormatted} in ${dateRangeText}.`
          })()}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Action Types Breakdown */}
        <Card className="bg-white dark:bg-card shadow-sm border border-gray-200 dark:border-border h-full flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-foreground">
              Actions by type
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            {actionTypeData.length > 0 ? (
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex-1 min-h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={actionTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius="70%"
                        fill="#7B94C6"
                        dataKey="value"
                      >
                        {actionTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: any, _: string, props: any) => {
                          const total = actionTypeData.reduce(
                            (sum, item) => sum + item.value,
                            0,
                          )
                          const percent =
                            total > 0 ? ((value / total) * 100).toFixed(1) : '0'
                          return [
                            `${formatNumber(value)} (${percent}%)`,
                            props.payload.name,
                          ]
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {actionTypeData.map((item, index) => {
                    const total = actionTypeData.reduce(
                      (sum, i) => sum + i.value,
                      0,
                    )
                    const percent =
                      total > 0 ? ((item.value / total) * 100).toFixed(1) : '0'
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between text-sm p-2 rounded hover:bg-gray-50 dark:hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-gray-700 dark:text-muted-foreground truncate">
                            {item.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <span className="text-xs text-gray-500 dark:text-muted-foreground">
                            {percent}%
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-foreground">
                            {formatNumber(item.value)}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              <Skeleton className="h-[200px] w-full animate-pulse flex-1" />
            )}
          </CardContent>
        </Card>

        {/* Workflows Breakdown */}
        <Card className="bg-white dark:bg-card shadow-sm border border-gray-200 dark:border-border h-full flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-foreground">
              Actions by rule (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            {workflowData.length > 0 ? (
              <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
                <table className="w-full border-collapse min-w-[300px]">
                  <thead>
                    <tr className="border-b-2 border-gray-200 dark:border-border">
                      <th className="text-left py-4 px-4 text-xs font-semibold text-gray-700 dark:text-muted-foreground uppercase tracking-wider">
                        Rule name
                      </th>
                      <th className="text-right py-4 px-4 text-xs font-semibold text-gray-700 dark:text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-border">
                    {workflowData.map((item, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50/50 dark:hover:bg-muted/30 transition-colors group"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm font-medium text-gray-900 dark:text-foreground">
                              {item.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-sm font-semibold text-gray-900 dark:text-foreground">
                            {formatNumber(item.count)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-muted-foreground py-8 text-center">
                No workflow data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
